import React, { useState, useMemo, useEffect } from 'react';
import {
  Autocomplete,
  Box, Typography, Button, IconButton, Chip, Paper, Collapse, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Select, FormControl, InputLabel, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip,
  CircularProgress, Alert, LinearProgress, Menu, ListItemIcon, ListItemText,
  FormControlLabel, Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Science as ScienceIcon,
  Biotech as BiotechIcon,
  Image as ImagingIcon,
  UploadFile as UploadIcon,
  Error as AbnormalIcon,
  FolderOpen as OutsideLabIcon,
  Assignment as OtherIcon,
  AttachFile as AttachIcon,
  OpenInNew as OpenIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  CameraAlt as ImagingMenuIcon,
  Edit as EditIcon,
  HealthAndSafety as CogginsIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { formatAnchoredDayOrInstant, formatCalendarDate, todayLocalISO, ymdToNoonUtcIso } from '../utils/dateUtils';
import { COGGINS_PANEL_NAME, isCogginsLabResultRow } from '../utils/coggins';
import { useClinicTimezone } from '../hooks/useClinicTimezone';
import { useSnackbar } from 'notistack';
import { useRBAC } from '../auth/useRBAC';
import { patientChartControls } from '../auth/pageControls';
import api from '../utils/api';
import DocumentViewerModal from './DocumentViewerModal';
import LabImportDialog from './LabImportDialog';
import ImagingRequestsPanel from './Keystone/ImagingRequestsPanel';
import ImagingStudyCard from './Keystone/ImagingStudyCard';
import NewImagingRequestDialog from './Keystone/NewImagingRequestDialog';
import { keystoneService, HorseStudySummary, KeystoneConfig } from '../services/keystoneService';
import { idexxService, IdexxConfig, LabRequest } from '../services/idexxService';
import LabRequestsPanel from './Idexx/LabRequestsPanel';
import NewLabRequestDialog from './Idexx/NewLabRequestDialog';
import { groupByDay } from '../utils/dateGrouping';
import { withOfflineFallback } from '../offline/offlineQuery';
import { useIsOffline } from '../offline/OfflineNotice';
import { OfflineNotice } from '../offline/OfflineNotice';
import { readByHorse, readActiveByHorse } from '../offline/readers';
import { clinicalArchiveService } from '../services/clinicalArchiveService';
import { executeWrite, executeUpdate, executeDelete, OfflineWriteBlockedError } from '../offline/executeWrite';

// ─── Types ────────────────────────────────────────────────────────────────────

type EntryCategory = 'all' | 'bloodwork' | 'imaging' | 'outside_lab' | 'other';

interface LabResultItem {
  testName: string;
  value: string;
  unit?: string;
  referenceRange?: string;
  isAbnormal?: boolean;
  notes?: string;
}

interface LabResult {
  id: string;
  __kind: 'bloodwork';
  labProvider: string;
  accessionNumber?: string;
  reportDate: string;
  /** CALENDAR DAY (`@db.Date`) — the day this result stops being valid, so it
   *  arrives as UTC midnight and is rendered with `formatCalendarDate`, never
   *  re-zoned. Null on every pre-existing row: the column shipped additive with
   *  no backfill, because a manufactured expiry would feed the reminder
   *  generator a due date it would EMAIL an owner about. */
  expiryDate?: string | null;
  /** The certificate — an `Attachment` id. Had zero writers and zero readers in
   *  the whole repo until this build; the column and its FK have been in prod
   *  since the IDEXX migration. */
  pdfAttachmentId?: string | null;
  /** The ORDERED panel's test codes, included on the list read expressly so the
   *  web can run the same three-probe Coggins classifier the backend runs. */
  labRequest?: { testCodes?: string[] | null } | null;
  panelName?: string;
  /** Delete archives since 2026-10; archived rows load only under "Show archived". */
  isArchived?: boolean;
  results: LabResultItem[];
  notes?: string;
  performedBy?: string;
  createdAt: string;
  // Provenance. ⚠ It is NOT the date discriminator any more — see the render.
  // `GET /lab-results` is an un-narrowed findMany (`lab-results.service.ts:217`)
  // that carries both the IDEXX poller's rows (`source: 'IDEXX'` +
  // `idexxResultId`, `idexx-result-mapper.service.ts:48`) and hand-entered ones
  // (`lab-results.service.ts:164`, `sync.service.ts:1476`, neither of which sets
  // either field). That told the ROWS apart but not their date KINDS: a poller
  // that sends a date-only `orderReceivedDate` writes a DAY, and provenance
  // cannot see the difference. The SHAPE can (DATE-KINDS §2.4).
  source?: string | null;
  idexxResultId?: string | null;
}

interface ImagingMeta {
  modality: string;       // Radiograph, Ultrasound, CT, MRI, Endoscopy, Scintigraphy, Other
  bodyRegion: string;
  numViews?: number;
  findings?: string;
  impression?: string;
  orderedBy?: string;
}

interface OutsideLabMeta {
  labProvider: string;
  accessionNumber?: string;
  panelName?: string;
  reportSummary?: string;
}

interface OtherDiagMeta {
  title: string;
  performedBy?: string;
  notes?: string;
}

interface DiagnosticRecord {
  id: string;
  __kind: 'imaging' | 'outside_lab' | 'other';
  type: string;
  date: string;
  notes?: string;           // JSON stringified metadata OR plain text
  performedBy?: string;
  createdAt: string;
  attachments?: any[];
}

// Keystone-mirrored imaging studies surface in the same unified timeline.
// Distinct __kind so the renderer dispatches to <ImagingStudyCard>, but the
// 'imaging' filter chip matches both this and legacy free-text imaging
// MedicalRecord rows.
interface KeystoneStudyEntry {
  id: string;
  __kind: 'imaging_study';
  study: HorseStudySummary;
  date: string; // mirrors HorseStudySummary.studyDate for sort
}

type DiagnosticEntry = LabResult | DiagnosticRecord | KeystoneStudyEntry;

// ─── Helper to parse stored JSON metadata from notes field ─────────────────

function parseMeta<T>(notes?: string): T | null {
  if (!notes) return null;
  try {
    const parsed = JSON.parse(notes);
    if (parsed && parsed.__meta) return parsed as T;
    return null;
  } catch { return null; }
}

const MODALITIES = ['Radiograph', 'Ultrasound', 'CT', 'MRI', 'Endoscopy', 'Scintigraphy', 'Electrocardiogram', 'Other'];

const KIND_CONFIG: Record<string, { label: string; color: 'primary' | 'secondary' | 'success' | 'warning' | 'info'; Icon: any }> = {
  // Not a timeline __kind — Coggins rows ARE LabResults and render as
  // bloodwork. This entry exists so the picker card and the dialog title can
  // colour/label the fourth quick-entry form.
  coggins:     { label: 'Coggins / EIA', color: 'secondary', Icon: CogginsIcon },
  bloodwork:   { label: 'Bloodwork',   color: 'primary',   Icon: BiotechIcon },
  imaging:     { label: 'Imaging',     color: 'info',      Icon: ImagingIcon },
  outside_lab: { label: 'Outside Lab', color: 'success',   Icon: OutsideLabIcon },
  other:       { label: 'Diagnostic',  color: 'warning',   Icon: OtherIcon },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

/**
 * Classify a lab panel from its (free-text) name into a short type badge.
 * Every imported lab lands in a single LabResult/BloodworkCard renderer, so
 * WITHOUT this every panel — urinalysis, fecal, Coggins — showed a hardcoded
 * "Bloodwork" chip. Driven off panelName (which the AI parser returns), this
 * makes the badge reflect what the document actually is.
 *
 * ⚠ ITS COGGINS ARM IS **NOT** THE COGGINS CLASSIFIER AND MUST NOT BE MERGED
 * WITH IT. This function answers "what SHAPE of document is this" and its
 * `/coggins|eia|titer|serolog|antibod/` arm deliberately lumps Coggins in
 * with every other serology so they all get one "Serology" chip. The identity
 * question — "is this row THE horse's Coggins", which drives the chart header,
 * the expiry and a client-facing reminder — is `utils/coggins.isCogginsLabResult`,
 * and it is narrower on purpose (an antibody titre is not a Coggins) and wider
 * where it counts (it also reads the analyte rows and the ordered test codes,
 * neither of which is a panel name). Two questions, two rules.
 */
function classifyLabPanel(panelName?: string): string {
  const p = (panelName || '').toLowerCase();
  if (!p) return 'Lab Panel';
  if (/urinalysis|urine|\bua\b/.test(p)) return 'Urinalysis';
  if (/fecal|egg count|\bfec\b|parasite|float/.test(p)) return 'Fecal';
  if (/coggins|\beia\b|titer|serolog|antibod/.test(p)) return 'Serology';
  if (/chemistr|metabolic|\bchem\b/.test(p)) return 'Chemistry';
  if (/cbc|complete blood|h[ae]matolog|blood count/.test(p)) return 'Bloodwork';
  if (/cytolog/.test(p)) return 'Cytology';
  if (/cultur|microbiolog|sensitiv/.test(p)) return 'Microbiology';
  if (/thyroid|cortisol|\bacth\b|insulin|endocrin/.test(p)) return 'Endocrine';
  return 'Lab Panel';
}

const BloodworkCard: React.FC<{
  entry: LabResult;
  /** Absent = the person may not archive a lab result (Delete archives since 2026-10). */
  onDelete?: () => void;
  /** Set on an archived row shown under "Show archived": renders Restore instead of Archive. */
  onRestore?: () => void;
  /** The Coggins certificate, resolved by the panel from the `lab_result`
   *  attachments query. Absent for every non-Coggins row and for a Coggins with
   *  no paperwork uploaded yet. */
  certificate?: any;
  /** Opens the quick-entry dialog in EDIT mode. Rendered only for a Coggins row
   *  — the accession, expiry and certificate are the three fields a human needs
   *  to correct, and they exist on no other kind of lab result. */
  onEditCoggins?: () => void;
}> = ({ entry, onDelete, onRestore, certificate, onEditCoggins }) => {
  const [expanded, setExpanded] = useState(false);
  const [certificateOpen, setCertificateOpen] = useState(false);
  /**
   * The certificate's bytes, fetched through the AUTHED client and handed to the
   * viewer as a `blob:` URL.
   *
   * ⚠ NOT `` `/api${certificate.storageUrl}` ``, which is the broken legacy idiom
   * two other call sites still use. Two things are wrong with it: there is no
   * `/api` route on the WEB origin (no CRA proxy, no nginx location, no Nest
   * global prefix — the API is a separate origin), and in PRODUCTION
   * `Attachment.storageUrl` is an ABSOLUTE S3 URL
   * (`s3-storage.service.ts` returns `https://<bucket>.s3.<region>.amazonaws.com/<key>`),
   * so the template literal concatenates to `/apihttps://…`. The house pattern is
   * `PatientFilesTab.handleView`: fetch `/attachments/:id/download` with
   * `responseType: 'blob'` and show the blob URL.
   */
  const [certificateUrl, setCertificateUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!certificateOpen || !certificate?.id) return;
    let revoked = false;
    let url: string | null = null;
    api
      .get(`/attachments/${certificate.id}/download`, { responseType: 'blob' })
      .then(res => {
        if (revoked) return;
        url = URL.createObjectURL(res.data);
        setCertificateUrl(url);
      })
      .catch(() => setCertificateOpen(false));
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
      setCertificateUrl(null);
    };
  }, [certificateOpen, certificate?.id]);
  // Is this row the horse's Coggins? Same three-probe classifier the reminder
  // generator uses (`utils/coggins`, the declared twin of `common/coggins`), so
  // a row the backend reminds on is a row this card decorates - and vice versa.
  const isCoggins = isCogginsLabResultRow(entry);
  // The zone an INSTANT on this card renders in: the clinic's once it is KNOWN,
  // the viewer's until then (ADR-0034). Only the instant ARM of the report-date
  // rule below consumes it — a day never touches a zone.
  const clinicTz = useClinicTimezone();
  const abnormal = (entry.results || []).filter(r => r.isAbnormal);
  const { enqueueSnackbar } = useSnackbar();
  // IDEXX-sourced results carry an idexxResultId; that unlocks the VC+ viewer
  // deep-link + the report PDF (both proxied through our backend).
  const resultId = entry.idexxResultId ?? null;
  // Reference-lab results stay linked to their LabRequest — surface the
  // requisition-form PDF here so it can be reprinted at any point in the
  // order's life, including AFTER it goes COMPLETE (when the order has
  // already left the pending strip).
  const isReflabResult =
    (entry as { modality?: string | null }).modality === 'REFLAB';
  const labRequestId =
    (entry as { labRequestId?: string | null }).labRequestId ?? null;
  const handleViewInVcp = async () => {
    try {
      const { url } = await idexxService.getResultViewUrl(resultId!);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      enqueueSnackbar('Could not open the VetConnect+ viewer.', { variant: 'error' });
    }
  };
  const handlePdf = async () => {
    try {
      await idexxService.openResultPdf(resultId!);
    } catch {
      enqueueSnackbar('Could not open the result PDF.', { variant: 'error' });
    }
  };
  const handleReprintReqForm = async () => {
    try {
      const { url } = await idexxService.getReqFormUrl(labRequestId!);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        enqueueSnackbar('Requisition form not available for this order.', { variant: 'warning' });
      }
    } catch {
      enqueueSnackbar('Could not reprint the requisition form.', { variant: 'error' });
    }
  };

  return (
    <Paper variant="outlined" sx={{ mb: 1.5, overflow: 'hidden' }}>
      <Box display="flex" alignItems="center" px={2} py={1.5} sx={{ cursor: 'pointer' }} onClick={() => setExpanded(x => !x)}>
        <BiotechIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 20 }} />
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2">{entry.panelName || 'Lab Panel'}</Typography>
            <Chip label={classifyLabPanel(entry.panelName)} size="small" color="primary" variant="outlined" />
            {abnormal.length > 0 && (
              <Chip icon={<WarningIcon />} label={`${abnormal.length} abnormal`} size="small" color="error" />
            )}
          </Box>
          {/* `data-testid` so the browser matrix can assert this VALUE rather than
            * its presence — e2e/specs/chart-panels.spec.ts renders the panel at
            * UTC-11, UTC and UTC+14 against two seeded rows of opposite kind. */}
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={`lab-result-caption-${entry.id}`}
          >
            {/* PER-ROW BY SHAPE, because `LabResult.reportDate` is MIXED
              * (DATE-KINDS §1.2) and this feed carries BOTH kinds:
              *
              *   hand-entered → the `bwForm.reportDate` `type="date"` field in
              *     this file's own dialog, posted as `new Date(value).toISOString()`
              *     = UTC midnight of a picked DAY. Date-part it, never re-zone it,
              *     or it prints the PREVIOUS day in every US zone AND disagrees
              *     with the `{kind:'calendar'}` day header above the card.
              *   IDEXX poller → `parseIsoOrNow(orderReceivedDate ?? updatedDate)`
              *     (`idexx-result-mapper.service.ts:43`) = whatever the provider
              *     sent. Date-parting a real stamp prints the NEXT day for an
              *     evening result — the inverse trap.
              *
              * ⚠ THE DISCRIMINATOR USED TO BE PROVENANCE (`source === 'IDEXX'`)
              * AND THAT WAS THE WRONG QUESTION. Provenance says which SYSTEM
              * wrote the row, not which KIND of value it wrote: a poller that
              * sends a date-only `orderReceivedDate` writes a DAY, and one that
              * sends a stamp writes a MOMENT — possibly in the same feed. That
              * left the IDEXX arm UNCERTAIN and therefore unfixable without
              * observing IDEXX's wire shape, which this repo cannot do.
              *
              * The SHAPE can be read from the value itself, and it is right in
              * BOTH readings: a stamp carrying a time takes the instant arm (a
              * moment, rendered in the clinic's zone), and a date-only string
              * parses to exact UTC midnight, which IS a day. Same rule, same
              * helper and same spelling set as `Payment.paymentDate`, the worked
              * example (`dateUtils.formatAnchoredDayOrInstant`; the backend twin
              * is `backend/src/common/payment-date.ts`).
              *
              * ⚠ Residual, unchanged: an IDEXX row whose stamp lands near
              * midnight still reads one day here and another in the UTC-bucketed
              * day header above the card (see the groupByDay note below). */}
            {entry.labProvider} ·{' '}
            {formatAnchoredDayOrInstant(entry.reportDate, clinicTz, 'MMM d, yyyy')}
            {entry.accessionNumber && ` · #${entry.accessionNumber}`}
            {entry.performedBy && ` · ${entry.performedBy}`}
            {/* ⚠ A DIFFERENT DATE RULE ONE LINE BELOW ITS NEIGHBOUR, ON PURPOSE.
              * `reportDate` above is MIXED and splits on the value's SHAPE;
              * `expiryDate` is a Postgres `date`, unambiguously a CALENDAR day,
              * so it goes straight through `formatCalendarDate` and never
              * touches `clinicTz`. Routing it through the shape helper would
              * work today and would be a lie about the column. */}
            {entry.expiryDate && (
              <span data-testid={`lab-result-expiry-${entry.id}`}>
                {' '}· expires {formatCalendarDate(entry.expiryDate, 'MM/dd/yy')}
              </span>
            )}
          </Typography>
        </Box>
        {resultId && (
          <>
            <Tooltip title="View in VetConnect+">
              <IconButton size="small" onClick={e => { e.stopPropagation(); handleViewInVcp(); }}>
                <OpenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Report PDF">
              <IconButton size="small" onClick={e => { e.stopPropagation(); handlePdf(); }}>
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        {isReflabResult && labRequestId && (
          <Tooltip title="Reprint requisition form">
            <IconButton size="small" onClick={e => { e.stopPropagation(); handleReprintReqForm(); }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {/* The CERTIFICATE. Deliberately NOT `handlePdf` above — that is
          * `idexxService.openResultPdf`, a VetConnect+ artifact fetched by
          * IDEXX result id. This is our own `Attachment`, and for a Coggins it
          * is the document a show gate or a buyer actually asks to see. */}
        {certificate?.id && (
          <Tooltip title="Coggins certificate">
            <IconButton
              size="small"
              data-testid={`lab-result-certificate-${entry.id}`}
              onClick={e => { e.stopPropagation(); setCertificateOpen(true); }}
            >
              <AttachIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {isCoggins && onEditCoggins && (
          <Tooltip title="Edit Coggins details">
            <IconButton
              size="small"
              data-testid={`lab-result-edit-${entry.id}`}
              onClick={e => { e.stopPropagation(); onEditCoggins(); }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {entry.isArchived && <Chip label="Archived" size="small" />}
        {onRestore && (
          <Tooltip title="Restore">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onRestore(); }}>
              <UnarchiveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="Archive">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete!(); }}>
              <ArchiveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      {certificate?.id && certificateUrl && (
        <DocumentViewerModal
          open={certificateOpen}
          onClose={() => setCertificateOpen(false)}
          url={certificateUrl}
          title={certificate.fileName || 'Coggins certificate'}
          mimeType={certificate.mimeType}
        />
      )}

      <Collapse in={expanded}>
        <Box px={2} pb={2}>
          {entry.results?.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Test</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Ref Range</TableCell>
                    <TableCell align="center">Flag</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entry.results.map((item, i) => (
                    <TableRow key={i} sx={item.isAbnormal ? { bgcolor: 'error.50' } : undefined}>
                      <TableCell>{item.testName}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {item.isAbnormal && <AbnormalIcon fontSize="inherit" color="error" />}
                          {item.value}
                        </Box>
                      </TableCell>
                      <TableCell>{item.unit || '—'}</TableCell>
                      <TableCell>{item.referenceRange || '—'}</TableCell>
                      <TableCell align="center">
                        {item.isAbnormal
                          ? <Chip label={abnormalChipLabel(item.value)} color="error" size="small" />
                          : <Chip label="WNL" color="success" size="small" variant="outlined" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : <Typography variant="body2" color="text.secondary">No individual test values recorded.</Typography>}
          {entry.notes && <Typography variant="body2" mt={1} color="text.secondary"><strong>Notes:</strong> {entry.notes}</Typography>}
        </Box>
      </Collapse>
    </Paper>
  );
};

const DiagnosticCard: React.FC<{ entry: DiagnosticRecord; onDelete?: () => void; attachment?: any }> = ({ entry, onDelete, attachment }) => {
  const [expanded, setExpanded] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const clinicTz = useClinicTimezone();
  const cfg = KIND_CONFIG[entry.__kind] || KIND_CONFIG.other;
  const Icon = cfg.Icon;

  const imagingMeta = entry.__kind === 'imaging' ? parseMeta<ImagingMeta & { __meta: string }>(entry.notes) : null;
  const outsideMeta = entry.__kind === 'outside_lab' ? parseMeta<OutsideLabMeta & { __meta: string }>(entry.notes) : null;
  const otherMeta = entry.__kind === 'other' ? parseMeta<OtherDiagMeta & { __meta: string }>(entry.notes) : null;

  // For migrated docs: notes = "[Imported Document] Title\nSource file: filename"
  const isImported = !imagingMeta && !outsideMeta && !otherMeta && entry.notes?.startsWith('[Imported Document]');
  const importedTitle = isImported
    ? entry.notes!.split('\n')[0].replace('[Imported Document] ', '').trim()
    : null;
  const plainNotes = (!imagingMeta && !outsideMeta && !otherMeta && !isImported) ? entry.notes : null;

  const title = imagingMeta
    ? `${imagingMeta.modality} — ${imagingMeta.bodyRegion}`
    : outsideMeta
      ? `${outsideMeta.panelName || 'Lab Report'} (${outsideMeta.labProvider})`
      : otherMeta
        ? otherMeta.title
        : importedTitle || (entry.notes?.slice(0, 60) || 'Diagnostic record');

  return (
    <Paper variant="outlined" sx={{ mb: 1.5, overflow: 'hidden' }}>
      <Box display="flex" alignItems="center" px={2} py={1.5} sx={{ cursor: 'pointer' }} onClick={() => setExpanded(x => !x)}>
        <Icon sx={{ mr: 1.5, color: `${cfg.color}.main`, fontSize: 20 }} />
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2">{title}</Typography>
            <Chip label={cfg.label} size="small" color={cfg.color} variant="outlined" />
            {imagingMeta?.numViews && <Chip label={`${imagingMeta.numViews} views`} size="small" variant="outlined" />}
            {isImported && <Chip label="Imported" size="small" variant="outlined" />}
            {attachment?.storageUrl && <Chip icon={<AttachIcon />} label="Document" size="small" color="primary" variant="outlined" />}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {/* PER-ROW BY SHAPE — the same rule as `BloodworkCard` above, on the other
              * MIXED column this panel renders. `MedicalRecord.date` carries both kinds:
              *
              *   this file's own dialog → `new Date(yyyy-MM-dd).toISOString()` at :541/:555/
              *     :569 = `T00:00:00.000Z`, a picked DAY. Date-part it.
              *   iOS `MedicalRecordFormView.swift:71` → a `[.date, .hourAndMinute]` picker,
              *     encoded `.iso8601` (`APIEndpoint.swift:36`). A real INSTANT, which takes
              *     the instant arm by not landing on an anchor. Render it in the clinic's zone.
              *
              * ⚠ THE DISCRIMINATOR IS THE UTC TIME-OF-DAY, NOT THE MILLISECOND. This comment
              * claimed for one day that iOS values are excluded because `.iso8601` emits no
              * fractional seconds while `toISOString()` always emits `.000`. That is true of
              * what iOS POSTS and false of what this card READS: `MedicalRecord.date` is a
              * Prisma `DateTime`, and every GET re-serialises it through `toISOString()`, so
              * `.000` is always present and carries no information. See
              * `pages/ClinicalRecords/chartDates.medicalRecordDateKind` for the full statement
              * and the RESIDUAL it accepts — an iOS moment on exactly 00:00:00 or 12:00:00 UTC
              * (8 PM / 8 AM Eastern, whole minute and second) reads as a day here and renders
              * a day late. Pinned as such by `LabsDiagnosticsPanel.test.tsx`.
              *
              * This card cannot receive a `visit_note` — the `med-records-diag` query below
              * asks for `types=diagnostic,imaging,outside_lab` (honoured server-side by
              * `medical-records.service.ts:50`) and its offline fallback filters the replica
              * to the same three — so the type override in `medicalRecordDateKind`, which
              * exists because a web visit note at 8 PM EDT IS `T00:00:00.000Z`, is not needed
              * here. HorseProfileClassic's record list, which DOES see visit notes, uses it.
              * That filter narrows the TYPES, not the WRITERS: iOS creates exactly these three
              * types too, so it cannot shrink the residual above. */}
            {formatAnchoredDayOrInstant(entry.date, clinicTz, 'MMM d, yyyy')}
            {(imagingMeta?.orderedBy || outsideMeta?.accessionNumber || otherMeta?.performedBy) && ' · '}
            {imagingMeta?.orderedBy || otherMeta?.performedBy || ''}
            {outsideMeta?.accessionNumber && `#${outsideMeta.accessionNumber}`}
          </Typography>
        </Box>
        {onDelete && (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete!(); }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={expanded}>
        <Box px={2} pb={2} display="flex" flexDirection="column" gap={1}>
          {imagingMeta && (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
              <Box><Typography variant="caption" color="text.secondary">Modality</Typography><Typography variant="body2">{imagingMeta.modality}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Body Region</Typography><Typography variant="body2">{imagingMeta.bodyRegion}</Typography></Box>
              {imagingMeta.findings && <Box gridColumn="1/-1"><Typography variant="caption" color="text.secondary">Findings</Typography><Typography variant="body2">{imagingMeta.findings}</Typography></Box>}
              {imagingMeta.impression && <Box gridColumn="1/-1"><Typography variant="caption" color="text.secondary">Impression / Diagnosis</Typography><Typography variant="body2" fontWeight={500}>{imagingMeta.impression}</Typography></Box>}
            </Box>
          )}
          {outsideMeta && (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
              <Box><Typography variant="caption" color="text.secondary">Lab Provider</Typography><Typography variant="body2">{outsideMeta.labProvider}</Typography></Box>
              {outsideMeta.panelName && <Box><Typography variant="caption" color="text.secondary">Panel</Typography><Typography variant="body2">{outsideMeta.panelName}</Typography></Box>}
              {outsideMeta.reportSummary && <Box gridColumn="1/-1"><Typography variant="caption" color="text.secondary">Summary</Typography><Typography variant="body2">{outsideMeta.reportSummary}</Typography></Box>}
            </Box>
          )}
          {otherMeta && (
            <Box display="flex" flexDirection="column" gap={1}>
              {otherMeta.performedBy && <Box><Typography variant="caption" color="text.secondary">Performed By</Typography><Typography variant="body2">{otherMeta.performedBy}</Typography></Box>}
              {otherMeta.notes && <Box><Typography variant="caption" color="text.secondary">Notes / Results</Typography><Typography variant="body2">{otherMeta.notes}</Typography></Box>}
            </Box>
          )}
          {plainNotes && <Typography variant="body2">{plainNotes}</Typography>}
          {attachment?.storageUrl && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenIcon />}
                onClick={() => setViewerOpen(true)}
                sx={{ mt: 1 }}
              >
                View Document
              </Button>
              <DocumentViewerModal
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                url={`/api${attachment.storageUrl}`}
                title={importedTitle || entry.notes || attachment.fileName || 'Document'}
                mimeType={attachment.mimeType}
              />
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ─── Add Dialog ───────────────────────────────────────────────────────────────

/**
 * The abnormal chip's wording. "H/L" (high / low) is what a flagged NUMERIC
 * analyte means; a qualitative answer such as a Positive Coggins is neither
 * high nor low, it is simply the abnormal result, and reads as such.
 */
export const abnormalChipLabel = (value: string | number | null | undefined): string =>
  /^\s*[<>≤≥]?\s*-?\d/.test(String(value ?? '')) ? 'H/L' : 'Abnormal';
const emptyRow = (): LabResultItem => ({ testName: '', value: '', unit: '', referenceRange: '', isAbnormal: false });

interface AddDialogProps {
  open: boolean;
  horseId: string;
  /**
   * When provided, skip the kind-picker step and jump straight into the
   * matching form. The unified "+ New order" menu in LabsDiagnosticsPanel
   * uses this so the user doesn't pick a kind twice (menu → picker).
   */
  initialKind?: 'bloodwork' | 'outside_lab' | 'other' | 'coggins';
  /**
   * When set, the dialog opens in EDIT mode on this Coggins `LabResult` rather
   * than creating one.
   *
   * This ships the FIRST web caller of `PUT /lab-results/:id`. The route has
   * existed, guarded (`manage:medical_records`), validated and synced since
   * before this build and had ZERO callers — which is the concrete reason the
   * manager's four fields belong on `LabResult` and not on a `Vaccination`:
   * there has never been a vaccination edit path on the web at all.
   */
  editTarget?: LabResult | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AddDiagnosticDialog: React.FC<AddDialogProps> = ({ open, horseId, initialKind, editTarget, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isOffline = useIsOffline();
  const [step, setStep] = useState<'pick' | 'form'>(initialKind ? 'form' : 'pick');
  const [kind, setKind] = useState<'bloodwork' | 'imaging' | 'outside_lab' | 'other' | 'coggins'>(initialKind ?? 'bloodwork');
  const [saving, setSaving] = useState(false);

  // Sync to initialKind when caller flips it (menu → dialog open).
  React.useEffect(() => {
    if (open && initialKind) {
      setKind(initialKind);
      setStep('form');
    } else if (open && !initialKind) {
      setStep('pick');
    }
  }, [open, initialKind]);

  // Bloodwork state
  const [bwForm, setBwForm] = useState({ labProvider: '', accessionNumber: '', reportDate: todayLocalISO(), panelName: '', performedBy: '', notes: '' });
  const [rows, setRows] = useState<LabResultItem[]>([emptyRow()]);

  // Imaging state
  const [imgForm, setImgForm] = useState({ modality: 'Radiograph', bodyRegion: '', numViews: '', findings: '', impression: '', orderedBy: '', date: todayLocalISO() });

  // Outside lab state
  const [olForm, setOlForm] = useState({ labProvider: '', accessionNumber: '', panelName: '', reportSummary: '', date: todayLocalISO() });

  // Other diagnostic state
  const [otherForm, setOtherForm] = useState({ title: '', notes: '', date: todayLocalISO(), performedBy: '' });

  /**
   * Coggins / EIA quick entry.
   *
   * ⚠ THE EXPIRY STARTS EMPTY AND STAYS EMPTY UNTIL SOMEONE TYPES ONE. It used
   * to pre-fill with +12 months and re-derive itself from the test date, and the
   * practice manager reviewing this feature asked for exactly the opposite:
   * validity is jurisdictional (several states accept 6 months, some events
   * require a test inside 6, a few run the clock from the DRAW date) and nothing
   * in this product knows the jurisdiction. A pre-filled guess reads as a fact
   * on the chart, feeds the recall generator a due date it will EMAIL an owner
   * about, and is silently wrong wherever the rule is not twelve months. Blank
   * says the honest thing: nobody has recorded it yet.
   */
  const emptyCogForm = () => ({
    labProvider: '',
    accessionNumber: '',
    reportDate: todayLocalISO(),
    expiryDate: '',
    /** The TESTING veterinarian — `LabResult.performedBy`, free text. Suggested
     *  from the clinic's vets but freeSolo, because an outside lab's vet signs
     *  plenty of these certificates and is not a user of this clinic. */
    performedBy: '',
    result: 'Negative',
  });
  const [cogForm, setCogForm] = useState(emptyCogForm);
  const [certificateFile, setCertificateFile] = useState<File | null>(null);

  /**
   * The clinic's veterinarians, as SUGGESTIONS for the testing-vet box.
   *
   * `GET /users/veterinarians` returns VETERINARIAN + OWNER rows (a sole
   * proprietor is their own vet) with `name` already composed server-side.
   * Deliberately NOT an id reference: `LabResult.performedBy` is a free-text
   * name column, and the vet on an outside lab's certificate has no user row
   * here at all. Only fetched while the Coggins form is open, and an empty list
   * (offline, error) simply leaves the field to typing.
   */
  const vetsQuery = useQuery(
    ['users', 'veterinarians'],
    () => api.get('/users/veterinarians').then(r => r.data),
    { enabled: open && kind === 'coggins', staleTime: 5 * 60 * 1000 },
  );
  const vetOptions = useMemo<string[]>(() => {
    const rows: any[] = Array.isArray(vetsQuery.data) ? vetsQuery.data : [];
    const names = rows
      .map(u => (u?.name || [u?.firstName, u?.lastName].filter(Boolean).join(' ') || '').trim())
      .filter(Boolean);
    return Array.from(new Set(names));
  }, [vetsQuery.data]);
  /**
   * What the form held when an EDIT opened, so the patch can carry only what a
   * human actually changed.
   *
   * ⚠ THIS IS NOT TIDINESS, IT IS DATA LOSS PREVENTION, and the row that proves
   * it is the IDEXX one. A whole-form patch would (a) rewrite `results` — real
   * provider analytes — with the single synthetic `{testName, value}` row this
   * form composes, and (b) re-spell `reportDate` as the noon day anchor when the
   * stored value is a real provider INSTANT, silently converting its KIND. Both
   * are unrecoverable, and neither field is what someone opened this dialog to
   * change: the point of the edit path is the accession, the expiry and the
   * certificate.
   */
  const [cogInitial, setCogInitial] = useState<Record<string, string> | null>(null);
  /** A row with more than one analyte came from a lab, not from this form — its
   *  Negative/Positive is not a single value this dialog may author. */
  const editHasAnalytes = (editTarget?.results?.length ?? 0) > 1;

  /** Prefill from the row being edited (accession / dates / result / provider). */
  React.useEffect(() => {
    if (!open || !editTarget) return;
    const resultRow = (editTarget.results || [])[0];
    const prefill = {
      labProvider: editTarget.labProvider || '',
      accessionNumber: editTarget.accessionNumber || '',
      // Both are CALENDAR days on this form; take the date PART so the input
      // shows the day that was stored, never the viewer's re-zoned version.
      reportDate: (editTarget.reportDate || '').slice(0, 10),
      expiryDate: (editTarget.expiryDate || '').slice(0, 10),
      performedBy: editTarget.performedBy || '',
      result: resultRow?.value || 'Negative',
    };
    setCogForm(prefill);
    setCogInitial(prefill);
    setCertificateFile(null);
    setKind('coggins');
    setStep('form');
  }, [open, editTarget]);

  const resetAll = () => {
    setStep('pick'); setKind('bloodwork'); setSaving(false);
    setBwForm({ labProvider: '', accessionNumber: '', reportDate: todayLocalISO(), panelName: '', performedBy: '', notes: '' });
    setRows([emptyRow()]);
    setImgForm({ modality: 'Radiograph', bodyRegion: '', numViews: '', findings: '', impression: '', orderedBy: '', date: todayLocalISO() });
    setOlForm({ labProvider: '', accessionNumber: '', panelName: '', reportSummary: '', date: todayLocalISO() });
    setOtherForm({ title: '', notes: '', date: todayLocalISO(), performedBy: '' });
    setCogForm(emptyCogForm());
    setCogInitial(null);
    setCertificateFile(null);
  };

  const handleClose = () => { resetAll(); onClose(); };

  /**
   * Upload the Coggins certificate and LINK it to the lab result.
   *
   * Three calls, in this order, because each needs the previous one's id:
   *   1. the LabResult exists (create or edit, above) -> `labResultId`
   *   2. `POST /attachments/upload` with `parentType: 'lab_result'` + that id
   *   3. `PUT /lab-results/:id { pdfAttachmentId }`
   *
   * ⚠ `LabResult.pdfAttachmentId` has been in the schema (and in prod) since the
   * IDEXX migration with ZERO writers and ZERO readers — a half-shipped column.
   * This is its first writer. Step 3 is tenancy-checked server-side, because a
   * bare FK only proves EXISTENCE and an unguarded one would let a lab result
   * point at another clinic's attachment, which the download route would then
   * serve.
   *
   * ONLINE ONLY, and it fails SOFT. The upload is a multipart S3 put with no
   * offline queue, and an offline create has a temp id that no attachment could
   * reference. The clinical row is already saved either way, so a failure here
   * warns and leaves the certificate to be added by editing the row — losing the
   * result because its PDF did not upload would be the worse trade.
   */
  const attachCertificate = async (labResultId?: string) => {
    if (!certificateFile || !labResultId) return;
    if (isOffline) {
      enqueueSnackbar('Saved. The certificate needs a connection — add it by editing the result.', { variant: 'info' });
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', certificateFile);
      formData.append('horseId', horseId);
      formData.append('category', 'lab_report');
      formData.append('parentType', 'lab_result');
      formData.append('parentId', labResultId);
      formData.append(
        'description',
        `Coggins certificate${cogForm.accessionNumber ? ` #${cogForm.accessionNumber}` : ''}`,
      );
      const attachment = await api
        .post('/attachments/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(r => r.data);
      if (attachment?.id) {
        await api.put(`/lab-results/${labResultId}`, { pdfAttachmentId: attachment.id });
      }
    } catch {
      enqueueSnackbar('The result saved, but the certificate did not upload.', { variant: 'warning' });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (kind === 'coggins') {
        if (!cogForm.reportDate) { enqueueSnackbar('Test date is required', { variant: 'warning' }); return; }
        /*
         * THE BODY, and the two date fields go out DIFFERENTLY on purpose.
         *
         *   reportDate  -> `ymdToNoonUtcIso` (`T12:00:00.000Z`). `LabResult.reportDate`
         *      is the MIXED column this panel reads with the SHAPE rule, and noon is
         *      the sanctioned day anchor there. The bloodwork branch below still posts
         *      `new Date(v).toISOString()` (UTC midnight, the other day spelling the
         *      shape rule accepts) — that is a known site with its own ratchet
         *      accounting and it is NOT unified here; a branch about Coggins is not
         *      where a date-idioms baseline should move.
         *   expiryDate  -> a BARE `yyyy-MM-dd`, never wrapped in `new Date()`.
         *      `LabResult.expiryDate` is a Postgres `date`, so Prisma takes the UTC
         *      date PART of whatever it is handed: a browser-local instant from an
         *      evening save would be truncated to the WRONG day, unrecoverably. The
         *      backend parses the bare string with `parseCalendarDay`, so no zone ever
         *      touches it in either direction.
         */
        const body: Record<string, any> = {
          horseId,
          labProvider: cogForm.labProvider || 'In-house',
          accessionNumber: cogForm.accessionNumber || undefined,
          reportDate: ymdToNoonUtcIso(cogForm.reportDate),
          expiryDate: cogForm.expiryDate || undefined,
          performedBy: cogForm.performedBy || undefined,
          panelName: COGGINS_PANEL_NAME,
          // A POSITIVE Coggins is the abnormal result of this test - the row must
          // carry the flag or the panel paints the same green WNL chip for both
          // answers (found on the v3.23.0 release walk-through). The edit path
          // reuses this array, so a corrected result re-flags itself too.
          results: [{ testName: COGGINS_PANEL_NAME, value: cogForm.result, isAbnormal: cogForm.result === 'Positive' }],
        };

        if (editTarget) {
          /*
           * A DIFF, not the whole form.
           *
           * `horseId` and `panelName` are never patched — an edit corrects the
           * paperwork on a result; it does not re-point it at another patient or
           * re-classify it. Everything else is sent ONLY if it changed, because
           * on an IDEXX row two of these fields are destructive to resend:
           * `results` holds the provider's analytes (this form composes a single
           * synthetic row), and `reportDate` holds a real provider INSTANT that
           * re-spelling as the noon day anchor would silently convert.
           *
           * `expiryDate` is the one field with three-valued meaning, and the
           * backend reads it exactly that way: OMITTED leaves the column alone,
           * `null` CLEARS it (and deletes the linked reminder, which loses its
           * basis), a day sets it.
           */
          const initial = cogInitial ?? {};
          const patch: Record<string, any> = {};
          if (cogForm.labProvider !== initial.labProvider) patch.labProvider = body.labProvider;
          if (cogForm.accessionNumber !== initial.accessionNumber) {
            // `null`, NOT `undefined`. This branch only runs because the value
            // CHANGED, and the change a user makes by emptying the box is a
            // deliberate clear — but axios omits an `undefined` value from the
            // JSON body entirely, so the request carried no key and the backend
            // read it as "leave it alone". The accession could not be erased.
            // `accessionNumber` is `String?` and its DTO rule is `@IsOptional()`,
            // which passes `null` through to Prisma.
            patch.accessionNumber = cogForm.accessionNumber || null;
          }
          if (cogForm.reportDate !== initial.reportDate) patch.reportDate = body.reportDate;
          if (cogForm.expiryDate !== initial.expiryDate) {
            patch.expiryDate = cogForm.expiryDate || null;
          }
          if (cogForm.performedBy !== initial.performedBy) {
            // `null`, not `undefined`, for the same reason as `accessionNumber`:
            // axios drops an `undefined` value from the body, so emptying the
            // box could never erase the stored name. `performedBy` is `String?`
            // and its DTO DECLARES `string | null` (not just `@IsOptional()`),
            // so the null is a stated part of the contract rather than a
            // validator accident, and it reaches Prisma as a clear.
            patch.performedBy = cogForm.performedBy || null;
          }
          if (!editHasAnalytes && cogForm.result !== initial.result) patch.results = body.results;
          if (Object.keys(patch).length > 0) {
            await executeUpdate({
              entityType: 'labResult',
              table: 'labResults',
              entityId: editTarget.id,
              payload: patch,
              networkFn: () => api.put(`/lab-results/${editTarget.id}`, patch).then(r => r.data),
            });
          }
          await attachCertificate(editTarget.id);
        } else {
          const created: any = await executeWrite({
            entityType: 'labResult',
            table: 'labResults',
            payload: body,
            networkFn: () => api.post('/lab-results', body).then(r => r.data),
          });
          await attachCertificate(created?.id);
        }
      } else if (kind === 'bloodwork') {
        if (!bwForm.labProvider || !bwForm.reportDate) { enqueueSnackbar('Lab provider and date required', { variant: 'warning' }); return; }
        const validRows = rows.filter(r => r.testName.trim() && r.value.trim());
        // The exact POST body — passed to BOTH executeWrite.payload (the
        // optimistic replica row when offline) and networkFn (the unchanged
        // online POST) so the two never diverge.
        const body = { horseId, ...bwForm, reportDate: new Date(bwForm.reportDate).toISOString(), results: validRows };
        await executeWrite({
          entityType: 'labResult',
          table: 'labResults',
          payload: body,
          networkFn: () => api.post('/lab-results', body).then(r => r.data),
        });
      } else if (kind === 'imaging') {
        if (!imgForm.bodyRegion) { enqueueSnackbar('Body region is required', { variant: 'warning' }); return; }
        const meta: ImagingMeta & { __meta: string } = {
          __meta: 'imaging', modality: imgForm.modality, bodyRegion: imgForm.bodyRegion,
          numViews: imgForm.numViews ? Number(imgForm.numViews) : undefined,
          findings: imgForm.findings || undefined, impression: imgForm.impression || undefined,
          orderedBy: imgForm.orderedBy || undefined,
        };
        const body = { horseId, type: 'imaging', date: new Date(imgForm.date).toISOString(), notes: JSON.stringify(meta) };
        await executeWrite({
          entityType: 'medicalRecord',
          table: 'medicalRecords',
          payload: body,
          networkFn: () => api.post('/medical-records', body).then(r => r.data),
        });
      } else if (kind === 'outside_lab') {
        if (!olForm.labProvider) { enqueueSnackbar('Lab provider is required', { variant: 'warning' }); return; }
        const meta: OutsideLabMeta & { __meta: string } = {
          __meta: 'outside_lab', labProvider: olForm.labProvider,
          accessionNumber: olForm.accessionNumber || undefined, panelName: olForm.panelName || undefined,
          reportSummary: olForm.reportSummary || undefined,
        };
        const body = { horseId, type: 'outside_lab', date: new Date(olForm.date).toISOString(), notes: JSON.stringify(meta) };
        await executeWrite({
          entityType: 'medicalRecord',
          table: 'medicalRecords',
          payload: body,
          networkFn: () => api.post('/medical-records', body).then(r => r.data),
        });
      } else {
        if (!otherForm.title) { enqueueSnackbar('Title is required', { variant: 'warning' }); return; }
        const meta: OtherDiagMeta & { __meta: string } = {
          __meta: 'other_diag', title: otherForm.title,
          performedBy: otherForm.performedBy || undefined,
          notes: otherForm.notes || undefined,
        };
        const body = { horseId, type: 'diagnostic', date: new Date(otherForm.date).toISOString(), notes: JSON.stringify(meta) };
        await executeWrite({
          entityType: 'medicalRecord',
          table: 'medicalRecords',
          payload: body,
          networkFn: () => api.post('/medical-records', body).then(r => r.data),
        });
      }
      enqueueSnackbar('Record saved', { variant: 'success' });
      resetAll();
      onSuccess();
    } catch (e) {
      if (e instanceof OfflineWriteBlockedError) { enqueueSnackbar(e.message, { variant: 'warning' }); }
      else { enqueueSnackbar('Failed to save record', { variant: 'error' }); }
    }
    finally { setSaving(false); }
  };

  // "Imaging" is intentionally NOT a kind here — Keystone-integrated imaging
  // requests go through the "+ New order ▾ → Imaging Study" path in the
  // unified header. Free-text imaging records (a legacy MedicalRecord row of
  // type='imaging') are no longer creatable from the UI; existing rows still
  // render correctly via DiagnosticCard.
  const KIND_OPTIONS: { value: typeof kind; label: string; desc: string; Icon: any }[] = [
    { value: 'coggins', label: 'Coggins / EIA Test', desc: 'Record the accession number, expiry and certificate', Icon: CogginsIcon },
    { value: 'bloodwork', label: 'Bloodwork / Lab Panel', desc: 'CBC, chemistry, urinalysis, titers — enter values row-by-row', Icon: BiotechIcon },
    { value: 'outside_lab', label: 'Outside Lab Report', desc: 'Report from IDEXX, Antech, or other external lab', Icon: OutsideLabIcon },
    { value: 'other',     label: 'Other Diagnostic', desc: 'ECG, biopsy, culture, cytology, or custom test', Icon: OtherIcon },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {step === 'pick'
          ? 'Add Lab / Diagnostic'
          : `${editTarget ? 'Edit' : 'Add'} ${KIND_OPTIONS.find(k => k.value === kind)?.label}`}
      </DialogTitle>
      <DialogContent dividers>
        {step === 'pick' ? (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={0.5}>
            {KIND_OPTIONS.map(opt => {
              const Icon = opt.Icon;
              const cfg = KIND_CONFIG[opt.value];
              return (
                <Paper
                  key={opt.value}
                  variant="outlined"
                  onClick={() => { setKind(opt.value); setStep('form'); }}
                  sx={{
                    p: 2, cursor: 'pointer', borderRadius: 2,
                    borderColor: 'divider',
                    '&:hover': { borderColor: `${cfg.color}.main`, bgcolor: `${cfg.color}.50` },
                    transition: 'all .15s',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Icon sx={{ color: `${cfg.color}.main`, fontSize: 28 }} />
                    <Box>
                      <Typography variant="subtitle2">{opt.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        ) : kind === 'coggins' ? (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={0.5}>
            <TextField
              label="Accession / Coggins #"
              size="small"
              value={cogForm.accessionNumber}
              onChange={e => setCogForm(f => ({ ...f, accessionNumber: e.target.value }))}
              placeholder="The number on the certificate"
              inputProps={{ 'data-testid': 'coggins-accession-input' }}
            />
            <TextField
              label="Lab"
              size="small"
              value={cogForm.labProvider}
              onChange={e => setCogForm(f => ({ ...f, labProvider: e.target.value }))}
              placeholder="In-house, Cornell, state lab…"
            />
            <TextField
              label="Test Date *"
              type="date"
              size="small"
              value={cogForm.reportDate}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'coggins-report-date-input' }}
              onChange={e => setCogForm(f => ({ ...f, reportDate: e.target.value }))}
            />
            <TextField
              label="Expires"
              type="date"
              size="small"
              value={cogForm.expiryDate}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'coggins-expiry-input' }}
              helperText="Leave blank until the certificate states it"
              onChange={e => setCogForm(f => ({ ...f, expiryDate: e.target.value }))}
            />
            {/* freeSolo — `LabResult.performedBy` is free text, and the vet who
              * signed a Coggins is often a state or outside lab's vet who has no
              * user account here. The clinic's own vets are only SUGGESTIONS, so
              * the list failing (offline, or a 403) costs nothing but typing. */}
            <Autocomplete
              freeSolo
              options={vetOptions}
              value={cogForm.performedBy}
              onInputChange={(_, value) => setCogForm(f => ({ ...f, performedBy: value }))}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Testing veterinarian"
                  size="small"
                  placeholder="Who signed the certificate"
                  inputProps={{ ...params.inputProps, 'data-testid': 'coggins-vet-input' }}
                />
              )}
            />
            {editHasAnalytes ? (
              // The row carries the LAB's analytes. A Negative/Positive dropdown
              // here would imply this dialog may author them, and saving it would
              // replace real provider values with one synthetic row. Show what is
              // stored and leave it alone — the accession, expiry and certificate
              // are what this form exists to correct.
              <TextField
                label="Result"
                size="small"
                value={`${editTarget?.results?.length ?? 0} analyte rows from the lab`}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                helperText="Reported values are not editable here"
                inputProps={{ 'data-testid': 'coggins-result-readonly' }}
              />
            ) : (
              <TextField
                label="Result"
                size="small"
                select
                value={cogForm.result}
                onChange={e => setCogForm(f => ({ ...f, result: e.target.value }))}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'coggins-result-input' }}
              >
                <option value="Negative">Negative</option>
                <option value="Positive">Positive</option>
              </TextField>
            )}
            <Box>
              <Button
                component="label"
                size="small"
                variant="outlined"
                startIcon={<UploadIcon />}
                disabled={isOffline}
                fullWidth
              >
                {certificateFile ? 'Certificate selected' : 'Attach certificate'}
                <input
                  type="file"
                  hidden
                  accept="application/pdf,image/*"
                  onChange={e => setCertificateFile(e.target.files?.[0] ?? null)}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                {certificateFile
                  ? certificateFile.name
                  : isOffline
                    ? 'Uploading needs a connection'
                    : 'PDF or photo of the signed form'}
              </Typography>
            </Box>
            <Alert severity="info" sx={{ gridColumn: '1/-1', py: 0.5 }}>
              Saving sets this horse's Coggins on the patient header and opens a
              recall reminder for the expiry date.
            </Alert>
          </Box>
        ) : kind === 'bloodwork' ? (
          <Box display="flex" flexDirection="column" gap={2} mt={0.5}>
            <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
              <TextField label="Lab Provider *" size="small" value={bwForm.labProvider} onChange={e => setBwForm(f => ({ ...f, labProvider: e.target.value }))} />
              <TextField label="Report Date *" type="date" size="small" value={bwForm.reportDate} onChange={e => setBwForm(f => ({ ...f, reportDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
              <TextField label="Panel Name" size="small" value={bwForm.panelName} onChange={e => setBwForm(f => ({ ...f, panelName: e.target.value }))} placeholder="e.g. CBC, Chemistry" />
              <TextField label="Accession #" size="small" value={bwForm.accessionNumber} onChange={e => setBwForm(f => ({ ...f, accessionNumber: e.target.value }))} />
              <TextField label="Ordered By" size="small" value={bwForm.performedBy} onChange={e => setBwForm(f => ({ ...f, performedBy: e.target.value }))} />
              <TextField label="Notes" size="small" value={bwForm.notes} onChange={e => setBwForm(f => ({ ...f, notes: e.target.value }))} />
            </Box>
            <Divider><Chip label="Test Results" size="small" /></Divider>
            {rows.map((row, i) => (
              <Box key={i} display="grid" gridTemplateColumns="2fr 1fr 1fr 2fr 1fr auto" gap={1} alignItems="center">
                <TextField size="small" placeholder="Test name *" value={row.testName} onChange={e => setRows(rs => rs.map((r, j) => j === i ? { ...r, testName: e.target.value } : r))} />
                <TextField size="small" placeholder="Value *" value={row.value} onChange={e => setRows(rs => rs.map((r, j) => j === i ? { ...r, value: e.target.value } : r))} />
                <TextField size="small" placeholder="Unit" value={row.unit} onChange={e => setRows(rs => rs.map((r, j) => j === i ? { ...r, unit: e.target.value } : r))} />
                <TextField size="small" placeholder="Ref range" value={row.referenceRange} onChange={e => setRows(rs => rs.map((r, j) => j === i ? { ...r, referenceRange: e.target.value } : r))} />
                <TextField size="small" select value={row.isAbnormal ? 'abn' : 'nml'} onChange={e => setRows(rs => rs.map((r, j) => j === i ? { ...r, isAbnormal: e.target.value === 'abn' } : r))} SelectProps={{ native: true }}>
                  <option value="nml">WNL</option>
                  <option value="abn">H / L</option>
                </TextField>
                <IconButton size="small" disabled={rows.length === 1} onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setRows(rs => [...rs, emptyRow()])}>Add Row</Button>
          </Box>
        ) : kind === 'imaging' ? (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={0.5}>
            <FormControl size="small">
              <InputLabel>Modality *</InputLabel>
              <Select value={imgForm.modality} label="Modality *" onChange={e => setImgForm(f => ({ ...f, modality: e.target.value }))}>
                {MODALITIES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Body Region / Location *" size="small" value={imgForm.bodyRegion} onChange={e => setImgForm(f => ({ ...f, bodyRegion: e.target.value }))} placeholder="e.g. Left front fetlock, Thorax" />
            <TextField label="Date *" type="date" size="small" value={imgForm.date} onChange={e => setImgForm(f => ({ ...f, date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="# of Views / Images" size="small" type="number" value={imgForm.numViews} onChange={e => setImgForm(f => ({ ...f, numViews: e.target.value }))} />
            <TextField label="Ordered By" size="small" value={imgForm.orderedBy} onChange={e => setImgForm(f => ({ ...f, orderedBy: e.target.value }))} />
            <Box /> {/* spacer */}
            <TextField label="Findings" size="small" multiline rows={2} fullWidth value={imgForm.findings} onChange={e => setImgForm(f => ({ ...f, findings: e.target.value }))} placeholder="Describe imaging findings..." sx={{ gridColumn: '1/-1' }} />
            <TextField label="Impression / Diagnosis" size="small" multiline rows={2} fullWidth value={imgForm.impression} onChange={e => setImgForm(f => ({ ...f, impression: e.target.value }))} placeholder="Radiologist/vet impression..." sx={{ gridColumn: '1/-1' }} />
          </Box>
        ) : kind === 'outside_lab' ? (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={0.5}>
            <TextField label="Lab Provider *" size="small" value={olForm.labProvider} onChange={e => setOlForm(f => ({ ...f, labProvider: e.target.value }))} placeholder="IDEXX, Antech, Cornell, etc." />
            <TextField label="Report Date *" type="date" size="small" value={olForm.date} onChange={e => setOlForm(f => ({ ...f, date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="Panel / Test Name" size="small" value={olForm.panelName} onChange={e => setOlForm(f => ({ ...f, panelName: e.target.value }))} placeholder="e.g. CBC, Fecal egg count" />
            <TextField label="Accession / Case #" size="small" value={olForm.accessionNumber} onChange={e => setOlForm(f => ({ ...f, accessionNumber: e.target.value }))} />
            <TextField label="Report Summary" size="small" multiline rows={3} value={olForm.reportSummary} onChange={e => setOlForm(f => ({ ...f, reportSummary: e.target.value }))} placeholder="Key findings from the report..." sx={{ gridColumn: '1/-1' }} />
            <Alert severity="info" sx={{ gridColumn: '1/-1', py: 0.5 }}>
              Tip: Attach the report PDF in the SOAP note's file upload field for full document storage.
            </Alert>
          </Box>
        ) : (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={0.5}>
            <TextField label="Test / Study Title *" size="small" value={otherForm.title} onChange={e => setOtherForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Fecal culture, Biopsy" />
            <TextField label="Date *" type="date" size="small" value={otherForm.date} onChange={e => setOtherForm(f => ({ ...f, date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="Performed By" size="small" value={otherForm.performedBy} onChange={e => setOtherForm(f => ({ ...f, performedBy: e.target.value }))} />
            <Box />
            <TextField label="Notes / Results" size="small" multiline rows={4} value={otherForm.notes} onChange={e => setOtherForm(f => ({ ...f, notes: e.target.value }))} sx={{ gridColumn: '1/-1' }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {/* No way BACK out of an edit. The picker would leave `editTarget` set
          * while `kind` moved to another form, and Save would then CREATE a row
          * from a dialog the user opened to correct one. */}
        {step === 'form' && !editTarget && <Button onClick={() => setStep('pick')} sx={{ mr: 'auto' }}>← Back</Button>}
        <Button onClick={handleClose}>Cancel</Button>
        {step === 'form' && (
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : undefined}>
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

interface Props {
  horseId: string;
}

const LabsDiagnosticsPanel: React.FC<Props> = ({ horseId }) => {
  const rbac = useRBAC();
  /**
   * ⚠ THE SECOND DEFECT THE PRODUCT OWNER FOUND: this whole panel had NO write gate.
   *
   * With both medical-record abilities off, "Add Lab / Diagnostic" (the +New order menu, its
   * four hand-entry items and the empty-state call to action) was still on the chart, and so
   * were the per-row Edit and Delete. Every one of them POSTs, PUTs or DELETEs to a route
   * declaring manage:medical_records — lab-results.controller.ts :36 / :103 / :128,
   * keystone.controller.ts :1211, idexx.controller.ts :654 — so each was a control whose first
   * click 403s. The panel mounts inside both chart layouts and takes only a horseId, so it
   * reads the map itself rather than being handed eight props.
   */
  const can = patientChartControls(rbac);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  // Reactive connectivity — re-renders on flips so the Keystone query gates and
  // disabled states recover after reconnect (render-time skipWhenOffline() went stale).
  const isOffline = useIsOffline();
  const [addOpen, setAddOpen] = useState(false);
  const [addInitialKind, setAddInitialKind] = useState<'bloodwork' | 'outside_lab' | 'other' | 'coggins' | null>(null);
  /** When set, AddDiagnosticDialog opens in EDIT mode on this Coggins row. */
  const [cogginsEditTarget, setCogginsEditTarget] = useState<LabResult | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [imagingRequestOpen, setImagingRequestOpen] = useState(false);
  /** When set, NewImagingRequestDialog opens in edit mode prefilled from this row. */
  const [imagingEditTarget, setImagingEditTarget] = useState<any>(null);
  const [labRequestOpen, setLabRequestOpen] = useState(false);
  /** When set, NewLabRequestDialog opens in edit mode prefilled from this row. */
  const [labEditTarget, setLabEditTarget] = useState<LabRequest | null>(null);
  const [orderMenuAnchor, setOrderMenuAnchor] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState<EntryCategory>('all');

  // Fetch bloodwork lab results — replicated entity, so prefer network but fall
  // back to the local replica when offline. (No skipWhenOffline gate: this read
  // must run offline.)
  const { data: labResults = [], isLoading: labLoading } = useQuery<any[]>(
    ['lab-results', horseId],
    withOfflineFallback(
      () => api.get('/lab-results', { params: { horseId } }).then(r => r.data),
      () => readActiveByHorse('labResults', horseId),
    ),
    { enabled: !!horseId },
  );

  // Archived lab results load only when asked for (Delete archives since 2026-10).
  const [showArchivedLabs, setShowArchivedLabs] = useState(false);
  const { data: archivedLabs = [] } = useQuery<any[]>(
    ['lab-results', horseId, 'archived'],
    withOfflineFallback(
      () => clinicalArchiveService.listArchived<any>('lab-results', horseId),
      async () => (await readByHorse('labResults', horseId)).filter((lr: any) => lr.isArchived),
    ),
    { enabled: !!horseId && showArchivedLabs },
  );
  const restoreLabMutation = useMutation(
    (id: string) => clinicalArchiveService.restore('lab-results', id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['lab-results', horseId]);
        enqueueSnackbar('Restored', { variant: 'success' });
      },
      onError: () => { enqueueSnackbar('Failed to restore', { variant: 'error' }); },
    },
  );

  // Fetch diagnostic medical records (imaging, outside_lab, diagnostic types).
  // Replicated entity → offline fallback to the replica. The replica holds ALL
  // medicalRecord rows for the horse, so filter to the diagnostic types here to
  // mirror the server-side `types` param.
  const { data: medRecords = [], isLoading: medLoading } = useQuery<any[]>(
    ['med-records-diag', horseId],
    withOfflineFallback(
      () => api.get('/medical-records', { params: { horseId, types: 'diagnostic,imaging,outside_lab' } }).then(r => r.data),
      async () => {
        const rows = await readByHorse('medicalRecords', horseId);
        return rows.filter((r: any) => ['diagnostic', 'imaging', 'outside_lab'].includes(r.type));
      },
    ),
    { enabled: !!horseId },
  );

  // Keystone-mirrored imaging studies. Same query key as the HorseProfile tab
  // badge so the count and the rendered list stay in sync. NOT replicated
  // offline — skip-when-offline gated (matching the parent badge queries) so
  // the panel doesn't fire doomed fetches; the timeline shows an inline notice
  // instead of silently shrinking.
  const { data: keystoneStudies = [], isLoading: studiesLoading } = useQuery<HorseStudySummary[]>(
    ['horse-keystone-studies', horseId],
    () => keystoneService.getStudiesForHorse(horseId),
    { enabled: !!horseId && !isOffline },
  );

  // Open imaging requests count (pending strip badge + tab badge in parent).
  // Keystone-backed → skip-when-offline gated, same as above.
  const { data: imagingRequests = [] } = useQuery<any[]>(
    ['imaging-requests', horseId],
    () => api.get('/keystone/imaging-requests', { params: { horseId } }).then(r => r.data),
    { enabled: !!horseId && !isOffline },
  );

  // Whether the clinic has Keystone configured + enabled via
  // Settings → Integrations. When not, the "+ New order → Imaging Study"
  // entry is disabled — clinicians can't push an MWL order to a Keystone
  // account that doesn't exist. Cached for 60s since this doesn't change
  // often within a session. Treated as "ready" while loading so the menu
  // doesn't briefly disable on first paint.
  // Offline the query is skipped (config stays undefined → "ready"), but the
  // Imaging Study menu item has an explicit offline-disabled branch below.
  const { data: keystoneConfig } = useQuery<KeystoneConfig>(
    ['keystone-config'],
    () => keystoneService.getConfig(),
    { staleTime: 60 * 1000, enabled: !isOffline },
  );
  const isKeystoneReady =
    keystoneConfig === undefined ||
    (keystoneConfig.configured === true && keystoneConfig.enabled !== false);

  // IDEXX VetConnect+ — same pattern as Keystone above. When configured +
  // enabled, the "Bloodwork (IDEXX)" menu entry is live; otherwise it shows
  // a tooltip pointing the user at Settings → Integrations. Lab requests
  // are queried so the pending strip + pendingCount stay in sync.
  const { data: idexxConfig } = useQuery<IdexxConfig>(
    ['idexx-config'],
    () => idexxService.getConfig(),
    { staleTime: 60 * 1000 },
  );
  const isIdexxReady =
    idexxConfig === undefined ||
    (idexxConfig.configured === true && idexxConfig.enabled !== false);
  const { data: labRequests = [] } = useQuery<LabRequest[]>(
    ['idexx-lab-requests', horseId],
    () => idexxService.listLabRequests(horseId),
    { enabled: !!horseId && isIdexxReady, refetchInterval: 30000 },
  );

  // Fetch attachments linked to medical records for this horse. Attachment
  // rows aren't replicated (manifest only) → skip-when-offline gated.
  const { data: diagAttachments = [] } = useQuery<any[]>(
    ['diag-attachments', horseId],
    () => api.get('/attachments', { params: { horseId, parentType: 'medical_record' } }).then(r => r.data),
    { enabled: !!horseId && !isOffline },
  );

  // Attachments hanging off a LAB RESULT — today that is the Coggins
  // certificate. A SECOND query rather than a widened one because `parentType`
  // is a single-value filter server-side, and its own react-query key keeps the
  // medical-record map (which other rows depend on) untouched. Attachment rows
  // are not replicated, so this is skip-when-offline like its sibling.
  const { data: labAttachments = [] } = useQuery<any[]>(
    ['lab-attachments', horseId],
    () => api.get('/attachments', { params: { horseId, parentType: 'lab_result' } }).then(r => r.data),
    { enabled: !!horseId && !isOffline },
  );

  // Map medical record id → attachment
  const diagAttachmentMap = useMemo(() => {
    const map: Record<string, any> = {};
    diagAttachments.forEach((a: any) => { if (a.parentId) map[a.parentId] = a; });
    return map;
  }, [diagAttachments]);

  /**
   * Lab result id → its certificate, resolved BY ID rather than by parentId
   * alone: `LabResult.pdfAttachmentId` is the authoritative link (it is what the
   * chart header reads and what the tenancy check guards), while `parentId` is
   * the un-scoped polymorphic breadcrumb the upload wrote. Preferring the FK
   * means the card and the header can never disagree about which file is the
   * certificate.
   */
  const labAttachmentMap = useMemo(() => {
    const byId: Record<string, any> = {};
    labAttachments.forEach((a: any) => { if (a.id) byId[a.id] = a; });
    const map: Record<string, any> = {};
    (labResults || []).forEach((lr: any) => {
      if (lr.pdfAttachmentId && byId[lr.pdfAttachmentId]) map[lr.id] = byId[lr.pdfAttachmentId];
    });
    return map;
  }, [labAttachments, labResults]);

  const deleteLabMutation = useMutation(
    (id: string) => executeDelete({
      entityType: 'labResult',
      table: 'labResults',
      entityId: id,
      networkFn: () => api.delete(`/lab-results/${id}`).then(r => r.data),
    }),
    {
      onSuccess: () => { queryClient.invalidateQueries(['lab-results', horseId]); enqueueSnackbar('Archived', { variant: 'success' }); },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError) { enqueueSnackbar(error.message, { variant: 'warning' }); return; }
        enqueueSnackbar('Failed to delete', { variant: 'error' });
      },
    },
  );

  const deleteMedMutation = useMutation(
    (id: string) => executeDelete({
      entityType: 'medicalRecord',
      table: 'medicalRecords',
      entityId: id,
      networkFn: () => api.delete(`/medical-records/${id}`).then(r => r.data),
    }),
    {
      onSuccess: () => { queryClient.invalidateQueries(['med-records-diag', horseId]); enqueueSnackbar('Deleted', { variant: 'success' }); },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError) { enqueueSnackbar(error.message, { variant: 'warning' }); return; }
        enqueueSnackbar('Failed to delete', { variant: 'error' });
      },
    },
  );

  // Merge and sort all entries newest-first. Three sources:
  //   - LabResult rows (bloodwork)
  //   - MedicalRecord rows of diagnostic types (legacy imaging / outside_lab / other)
  //   - HorseStudySummary rows (Keystone-mirrored imaging studies)
  const allEntries = useMemo<DiagnosticEntry[]>(() => {
    const bloodwork: LabResult[] = (labResults || []).map((lr: any) => ({ ...lr, __kind: 'bloodwork' as const }));
    const diagnostics: DiagnosticRecord[] = (medRecords || []).map((r: any) => ({
      ...r,
      __kind: (r.type === 'imaging' ? 'imaging' : r.type === 'outside_lab' ? 'outside_lab' : 'other') as any,
    }));
    const studies: KeystoneStudyEntry[] = (keystoneStudies || []).map((s: HorseStudySummary) => ({
      id: s.studyId,
      __kind: 'imaging_study' as const,
      study: s,
      date: s.studyDate ?? new Date(0).toISOString(),
    }));
    return [...bloodwork, ...diagnostics, ...studies].sort((a, b) => {
      const dateA = 'reportDate' in a ? a.reportDate : (a as DiagnosticRecord | KeystoneStudyEntry).date;
      const dateB = 'reportDate' in b ? b.reportDate : (b as DiagnosticRecord | KeystoneStudyEntry).date;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }, [labResults, medRecords, keystoneStudies]);

  // 'imaging' filter chip matches both legacy free-text imaging records
  // (kind='imaging') and Keystone-mirrored studies (kind='imaging_study').
  const matchesFilter = (e: DiagnosticEntry) => {
    if (filter === 'all') return true;
    if (filter === 'imaging') return e.__kind === 'imaging' || e.__kind === 'imaging_study';
    return e.__kind === filter;
  };

  const filtered = useMemo(() => allEntries.filter(matchesFilter), [allEntries, filter]);

  const counts = useMemo(() => ({
    all:         allEntries.length,
    bloodwork:   allEntries.filter(e => e.__kind === 'bloodwork').length,
    imaging:     allEntries.filter(e => e.__kind === 'imaging' || e.__kind === 'imaging_study').length,
    outside_lab: allEntries.filter(e => e.__kind === 'outside_lab').length,
    other:       allEntries.filter(e => e.__kind === 'other').length,
  }), [allEntries]);

  // Pending = open imaging requests + open IDEXX lab requests. The union
  // keeps the "M pending" badge accurate when either integration has
  // in-flight work for this horse.
  const pendingCount = useMemo(() => {
    const imaging = imagingRequests.filter(
      (r: any) => r.submissionStatus === 'DRAFT' || r.submissionStatus === 'SUBMITTED',
    ).length;
    const labs = labRequests.filter(
      (r) => ['DRAFT', 'CREATED', 'SUBMITTED', 'AT_LAB', 'PARTIAL', 'FAILED'].includes(r.status),
    ).length;
    return imaging + labs;
  }, [imagingRequests, labRequests]);

  const loading = labLoading || medLoading || studiesLoading;

  const handleSuccess = () => {
    queryClient.invalidateQueries(['lab-results', horseId]);
    queryClient.invalidateQueries(['med-records-diag', horseId]);
    // The certificate lands in a different table AND a different query than the
    // result it belongs to, so a save that uploaded one leaves a stale map
    // unless this refetches too.
    queryClient.invalidateQueries(['lab-attachments', horseId]);
    setAddOpen(false);
    setAddInitialKind(null);
    setCogginsEditTarget(null);
  };

  const openAddDialog = (kind: 'bloodwork' | 'outside_lab' | 'other' | 'coggins') => {
    setCogginsEditTarget(null);
    setAddInitialKind(kind);
    setAddOpen(true);
    setOrderMenuAnchor(null);
  };

  const openCogginsEdit = (entry: LabResult) => {
    setCogginsEditTarget(entry);
    setAddInitialKind('coggins');
    setAddOpen(true);
  };

  const totalCount = counts.all + pendingCount;

  return (
    <Box>
      {/*
        Unified Labs & Diagnostics layout.

          ┌──────────────────────────────────────────────────┐
          │  N item(s) (M pending)    [Import] [+ New order ▾]│  ← header
          │  All │ Imaging │ Bloodwork │ Outside Lab │ Other  │  ← filter chips
          ├──────────────────────────────────────────────────┤
          │  Imaging Requests   (pending strip — always on)  │
          ├──────────────────────────────────────────────────┤
          │  Results timeline   (filter-aware)               │
          │   ▎ Imaging Study   📷  thumbnails / report      │
          │   ▎ Bloodwork       🧪  expand for values         │
          │   ▎ Outside Lab     📁                            │
          └──────────────────────────────────────────────────┘

        IDEXX integration points (frontend dev: see comment block below the
        +New order menu and inside `allEntries`).
      */}

      {/* Header — counts + import + unified +New order menu */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
        <Typography variant="subtitle2" color="text.secondary">
          {totalCount} item{totalCount !== 1 ? 's' : ''}
          {pendingCount > 0 && (
            <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
              · {pendingCount} pending
            </Typography>
          )}
        </Typography>
        <Box display="flex" gap={1}>
          {/* ⚠ HIDDEN, NOT DISABLED, when the person lacks the slug — but still DISABLED when
              the only problem is the network. The two states are different sentences: "not for
              you" has no cure on this screen, "not right now" does. */}
          {can.labWrite && (<>
          {/* Import = S3 upload + server/AI parse — online-only. Disable the
              trigger offline so it fails clearly instead of breaking. */}
          <Tooltip title={!isOffline ? '' : 'Importing a document requires a connection'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => setImportOpen(true)}
                disabled={isOffline}
              >
                Import
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={e => setOrderMenuAnchor(e.currentTarget)}
          >
            New order
          </Button>
          <Menu
            anchorEl={orderMenuAnchor}
            open={!!orderMenuAnchor}
            onClose={() => setOrderMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {isOffline ? (
              // Offline — imaging orders push an MWL order to Keystone, which
              // can't be queued. Same disabled-MenuItem+Tooltip pattern as the
              // not-configured branch below.
              <Tooltip
                title="Imaging orders push to Keystone — reconnect to order."
                placement="left"
              >
                <span>
                  <MenuItem disabled>
                    <ListItemIcon><ImagingMenuIcon fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Imaging Study"
                      secondary="Needs a connection"
                    />
                  </MenuItem>
                </span>
              </Tooltip>
            ) : isKeystoneReady ? (
              <MenuItem
                onClick={() => {
                  setOrderMenuAnchor(null);
                  setImagingRequestOpen(true);
                }}
              >
                <ListItemIcon><ImagingMenuIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Imaging Study" secondary="Push MWL order to Keystone" />
              </MenuItem>
            ) : (
              // Disabled state — Keystone not configured for this clinic. MUI
              // requires the wrapping span so the Tooltip can capture hover
              // events on a disabled MenuItem (pointer events are otherwise
              // skipped). Mirrors the "Bloodwork (IDEXX) — Coming soon"
              // pattern below.
              <Tooltip
                title="Configure Keystone in Settings → Integrations to enable imaging requests."
                placement="left"
              >
                <span>
                  <MenuItem disabled>
                    <ListItemIcon><ImagingMenuIcon fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Imaging Study"
                      secondary="Keystone not configured"
                    />
                  </MenuItem>
                </span>
              </Tooltip>
            )}
            {isIdexxReady ? (
              <MenuItem
                onClick={() => {
                  setOrderMenuAnchor(null);
                  setLabEditTarget(null);
                  setLabRequestOpen(true);
                }}
              >
                <ListItemIcon><BiotechIcon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Bloodwork (IDEXX)"
                  secondary="Send requisition to IDEXX, poll for results"
                />
              </MenuItem>
            ) : (
              <Tooltip
                title="Configure IDEXX in Settings → Integrations to enable lab requests."
                placement="left"
              >
                <span>
                  <MenuItem disabled>
                    <ListItemIcon><BiotechIcon fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Bloodwork (IDEXX)"
                      secondary="IDEXX not configured"
                    />
                  </MenuItem>
                </span>
              </Tooltip>
            )}
            <Divider />
            <MenuItem onClick={() => openAddDialog('coggins')}>
              <ListItemIcon><CogginsIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Coggins / EIA" secondary="Accession number, expiry and certificate" />
            </MenuItem>
            <MenuItem onClick={() => openAddDialog('bloodwork')}>
              <ListItemIcon><BiotechIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Bloodwork (manual entry)" secondary="Type values from a paper report" />
            </MenuItem>
            <MenuItem onClick={() => openAddDialog('outside_lab')}>
              <ListItemIcon><OutsideLabIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Outside Lab Report" secondary="Antech, Cornell, UC Davis, etc." />
            </MenuItem>
            <MenuItem onClick={() => openAddDialog('other')}>
              <ListItemIcon><OtherIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Other Diagnostic" secondary="ECG, biopsy, culture, cytology" />
            </MenuItem>
          </Menu>
          </>)}
        </Box>
      </Box>

      {/* Lab Import Dialog */}
      <LabImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        horseId={horseId}
        onImported={() => {
          queryClient.invalidateQueries(['lab-results', horseId]);
          setImportOpen(false);
        }}
      />

      {/* Imaging request dialog — mounted by parent so the +New order menu
          AND the per-row Edit button on DRAFT pending entries share a
          single dialog. `editTarget` flips it into edit mode (prefilled). */}
      <NewImagingRequestDialog
        open={imagingRequestOpen}
        horseId={horseId}
        editTarget={imagingEditTarget}
        onClose={() => {
          setImagingRequestOpen(false);
          setImagingEditTarget(null);
        }}
        onSuccess={() => {
          setImagingRequestOpen(false);
          setImagingEditTarget(null);
          queryClient.invalidateQueries(['imaging-requests', horseId]);
        }}
      />

      {/* IDEXX lab request dialog — same parent-mounted pattern as the
          imaging dialog above. Triggered from the +New order menu and
          per-row Edit on DRAFT/CREATED/FAILED rows in LabRequestsPanel. */}
      <NewLabRequestDialog
        open={labRequestOpen}
        horseId={horseId}
        editTarget={labEditTarget}
        onClose={() => {
          setLabRequestOpen(false);
          setLabEditTarget(null);
        }}
        onSuccess={() => {
          setLabRequestOpen(false);
          setLabEditTarget(null);
          queryClient.invalidateQueries(['idexx-lab-requests', horseId]);
        }}
      />

      {/* Keystone studies, pending orders, and document links aren't replicated
          — offline the timeline shows only locally stored results. Renders
          nothing while online or flag-off. */}
      <OfflineNotice sx={{ mb: 1.5 }}>
        Imaging studies and document links need a connection — showing locally
        stored results only.
      </OfflineNotice>

      {/* Filter chips */}
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        {([
          { key: 'all',         label: 'All',        count: counts.all },
          { key: 'imaging',     label: 'Imaging',    count: counts.imaging },
          { key: 'bloodwork',   label: 'Bloodwork',  count: counts.bloodwork },
          { key: 'outside_lab', label: 'Outside Lab',count: counts.outside_lab },
          { key: 'other',       label: 'Other',      count: counts.other },
        ] as { key: EntryCategory; label: string; count: number }[]).map(opt => (
          <Chip
            key={opt.key}
            label={`${opt.label}${opt.count > 0 ? ` (${opt.count})` : ''}`}
            onClick={() => setFilter(opt.key)}
            color={filter === opt.key ? (opt.key === 'all' ? 'primary' : KIND_CONFIG[opt.key]?.color || 'primary') : 'default'}
            variant={filter === opt.key ? 'filled' : 'outlined'}
            size="small"
          />
        ))}
      </Box>

      {/* Results timeline — finalized results render FIRST; the pending /
          in-flight order strips are moved below so completed work surfaces at
          the top of the chart. */}
      {loading ? (
        <LinearProgress sx={{ borderRadius: 1 }} />
      ) : filtered.length === 0 ? (
        <Box py={6} textAlign="center">
          <ScienceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {filter === 'all' ? 'No results yet' : `No ${filter.replace('_', ' ')} records`}
          </Typography>
          {/* The empty state keeps its text and loses its call to action. */}
          {can.labWrite && (
            <Button size="small" sx={{ mt: 1 }} startIcon={<AddIcon />} endIcon={<ArrowDropDownIcon />}
              onClick={e => setOrderMenuAnchor(e.currentTarget)}>
              New order
            </Button>
          )}
        </Box>
      ) : (
        // Bucket entries by calendar day. The newest day floats to top with
        // its label ("Today" / "Yesterday" / "Tue, Apr 28, 2026"); within a
        // day the entries keep their newest-first order from `filtered`.
        //
        // kind: 'calendar' — the date PART, never re-zoned. Both grouped fields
        // (`LabResult.reportDate`, `MedicalRecord.date`) are MIXED at the column
        // level (DATE-KINDS §1.2). This panel's own hand-entry forms post
        // `type="date"` values, and for THOSE rows `BloodworkCard` reads
        // `reportDate` from its date part (the DAY arm of
        // `formatAnchoredDayOrInstant`) — so that arm agrees with the bucket.
        //
        // ⚠ A `reportDate` that is a real STAMP does not, on purpose. Since the
        // card branches on the value's SHAPE (§2.4), such a row renders in the
        // CLINIC's zone while this header still buckets by the UTC date part, so
        // a result stamped after 8 PM Eastern sits under the next day's header.
        // Closing that means bucketing by clinic-zone day for the instant rows —
        // a change to this page's timeline, not to a render, and it is the same
        // open item the keystone note below describes.
        //
        // ⚠ `DiagnosticCard` now takes the SAME shape rule, on `MedicalRecord.date`
        // (also MIXED per row — this file's dialog picks a day, iOS writes a
        // moment). Its day arm agrees with this bucket exactly; its instant arm
        // has the same residual as the bloodwork one above — an iOS-written
        // evening record renders its clinic-zone day on the card and sits under
        // the UTC-bucketed header. Same open item, same fix (bucket the instant
        // rows by clinic-zone day), not a new one.
        //
        // ⚠ KNOWN MISMATCH, pre-existing and NOT fixed here: the third feed,
        // `KeystoneStudyEntry.date` (= `studyDate`), is an INSTANT, and since
        // 2026-09-02 the ImagingStudyCard inside renders it in the CLINIC zone
        // while this bucket still uses the UTC date part — so a study stamped
        // after 8 PM ET sits under the next day's header. Fixing it means
        // bucketing the keystone entries by clinic-zone day (a second groupByDay
        // pass, or a kind-per-entry option), which is a change to this page's
        // timeline rather than to a render. Tracked in DATE-KINDS §2.3.
        groupByDay(
          filtered,
          e =>
            'reportDate' in e
              ? e.reportDate
              : (e as DiagnosticRecord | KeystoneStudyEntry).date,
          { kind: 'calendar' },
        ).map(group => (
          <React.Fragment key={group.dayKey}>
            <Box display="flex" alignItems="center" gap={1} mt={2} mb={0.5}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {group.dayLabel}
              </Typography>
              <Box sx={{ flex: 1, borderBottom: '1px dashed', borderColor: 'divider' }} />
              <Typography variant="caption" color="text.secondary">
                {group.items.length} {group.items.length === 1 ? 'item' : 'items'}
              </Typography>
            </Box>
            {group.items.map(entry => {
              if (entry.__kind === 'bloodwork') {
                const lab = entry as LabResult;
                return (
                  <BloodworkCard
                    key={lab.id}
                    entry={lab}
                    certificate={labAttachmentMap[lab.id]}
                    onEditCoggins={can.labWrite ? () => openCogginsEdit(lab) : undefined}
                    onDelete={can.removeClinicalRecord ? () => deleteLabMutation.mutate(lab.id) : undefined}
                  />
                );
              }
              if (entry.__kind === 'imaging_study') {
                const ks = entry as KeystoneStudyEntry;
                return (
                  <ImagingStudyCard
                    key={ks.id}
                    accessionNumber={ks.study.accessionNumber}
                    onRefresh={() => queryClient.invalidateQueries(['horse-keystone-studies', horseId])}
                  />
                );
              }
              return <DiagnosticCard key={entry.id} entry={entry as DiagnosticRecord} onDelete={can.removeRecord ? () => deleteMedMutation.mutate(entry.id) : undefined} attachment={diagAttachmentMap[entry.id]} />;
            })}
          </React.Fragment>
        ))
      )}

      {(filter === 'all' || filter === 'bloodwork') && (
        <Box mt={2}>
          <FormControlLabel
            control={<Switch size="small" checked={showArchivedLabs} onChange={e => setShowArchivedLabs(e.target.checked)} />}
            label="Show archived lab results"
          />
          {showArchivedLabs && archivedLabs.length === 0 && (
            <Typography variant="body2" color="text.secondary">No archived lab results</Typography>
          )}
          {showArchivedLabs && archivedLabs.map((lab: any) => (
            <BloodworkCard
              key={lab.id}
              entry={{ ...lab, __kind: 'bloodwork' }}
              onRestore={can.removeRecord ? () => restoreLabMutation.mutate(lab.id) : undefined}
            />
          ))}
        </Box>
      )}

      {/* Pending / in-flight order strips — shown BELOW finalized results so
          completed work surfaces first. Panels hide their own header + add
          button; the unified header above is the single source of truth.
          Gated by filter so Bloodwork / Outside Lab / Other don't show an
          unrelated imaging strip. */}
      {(filter === 'all' || filter === 'imaging') && (
        <ImagingRequestsPanel
          horseId={horseId}
          hideAddButton
          hideHeader
          onEdit={can.labWrite ? (req: any) => {
            setImagingEditTarget(req);
            setImagingRequestOpen(true);
          } : undefined}
        />
      )}

      {(filter === 'all' || filter === 'bloodwork') && (
        <LabRequestsPanel
          horseId={horseId}
          hideHeader
          onEdit={can.labWrite ? (req: any) => {
            setLabEditTarget(req);
            setLabRequestOpen(true);
          } : undefined}
        />
      )}

      <AddDiagnosticDialog
        open={addOpen}
        horseId={horseId}
        initialKind={addInitialKind ?? undefined}
        editTarget={cogginsEditTarget}
        onClose={() => { setAddOpen(false); setAddInitialKind(null); setCogginsEditTarget(null); }}
        onSuccess={handleSuccess}
      />
    </Box>
  );
  /*
    ════════════════════════════════════════════════════════════════════
    IDEXX UI INTEGRATION GUIDE
    ════════════════════════════════════════════════════════════════════
    Once the backend ships:
      - LabRequest table (mirrors ImagingRequest: DRAFT/SUBMITTED/CANCELLED)
      - LabRequestsService (createDraft, submit, cancel, deleteDraft)
      - IDEXX poller writing finalized panels into LabResult rows

    Frontend changes required (search this file for "IDEXX integration point"):

    1. Wire the +New order menu's "Bloodwork (IDEXX)" entry:
       - Build NewLabRequestDialog (analog of NewImagingRequestDialog) with
         panel/test picker, clinical history, priority, draft vs submit.
       - Mount it from this file, opened by the menu item.

    2. Add LabRequestsPanel (analog of ImagingRequestsPanel) and mount it
       below the imaging pending strip (see integration point #2 in render
       output). Both should accept hideAddButton + hideHeader so the unified
       outer header drives ordering / counts.

    3. Update pendingCount in this component to union open lab requests:
         pendingCount = openImagingRequests + openLabRequests

    4. Update labsCount in HorseProfile.tsx tab badge to add open
       lab requests. Same pattern as keystoneStudiesForCount +
       openImagingRequestsCount already wired there.

    5. Lab results from the IDEXX poller already land in /lab-results, so
       the existing BloodworkCard renderer in this file picks them up
       automatically — no UI change needed for the result side.
    ════════════════════════════════════════════════════════════════════
  */
};

export default LabsDiagnosticsPanel;
